import React, { useState, useEffect, useRef } from "react";
import { Modal, Form } from "react-bootstrap";
import CommonButton from "../../commonButton";
import { formatMomentDate } from "../../../utils/dateHelper";
import withModal from "../../withModal";
import { useToast } from "../../../context/TostContext";
import InputField from "../../form/InputField";

const CompanyAwardModalInner = ({ onClose, onSubmit, award, isLoading }) => {
  const [formData, setFormData] = useState({
    title: "",
    awarded_by: "",
    award_date: "",
    description: "",
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [removeFile, setRemoveFile] = useState(false);
  const fileInputRef = useRef(null);
  const toast=useToast();
  useEffect(() => {
    if (award) {
      setFormData({
        title: award.title || "",
        awarded_by: award.awarded_by || "",
        award_date: award.award_date ? formatMomentDate(award.award_date, "YYYY-MM-DD") : "",
        description: award.description || "",
      });
      setFile(null);
      setFilePreview(award.file?.url || null);
      setRemoveFile(false);
    } else {
      setFormData({
        title: "",
        awarded_by: "",
        award_date: "",
        description: "",
      });
      setFile(null);
      setFilePreview(null);
      setRemoveFile(false);
    }
  }, [award]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        return;
      }
      setFile(selectedFile);
      setFilePreview(URL.createObjectURL(selectedFile));
      setRemoveFile(false);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFilePreview(null);
    setRemoveFile(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!formData.title) {
      toast.error("Please enter a title");
      return;
    }

    if (!formData.awarded_by) {
      toast.error("Please enter who awarded this");
      return;
    }

    if (!formData.award_date) {
      toast.error("Please enter an award date");
      return;
    }

    const todayStr = new Date().toLocaleDateString('en-CA');
    if (formData.award_date && formData.award_date > todayStr) {
      toast.error("Award date cannot be in the future");
      return;
    }

    const submitData = new FormData();
    submitData.append("title", formData.title);
    submitData.append("awarded_by", formData.awarded_by);
    if (formData.award_date) {
      submitData.append("award_date", formData.award_date);
    }
    submitData.append("description", formData.description);

    if (file) {
      submitData.append("file", file);
    } else if (removeFile) {
      submitData.append("remove_file", "true");
    }

    await onSubmit(submitData);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Modal.Body className="grid row-gap-3">
        <InputField
          className="g-col-12"
          label={{ name: "Title" }}
          required
          input={{
            type: "text",
            name: "title",
            value: formData.title,
            onChange: handleChange,
            placeholder: "Ex: Top Tech Company 2024",
          }}
        />
        <InputField
          className="g-col-6"
          label={{ name: "Issuer" }}
          required
          input={{
            type: "text",
            name: "awarded_by",
            value: formData.awarded_by,
            onChange: handleChange,
            placeholder: "Ex: Forbes",
          }}
        />
        <InputField
          className="g-col-6"
          label={{ name: "Issue Date" }}
          required
          input={{
            type: "date",
            name: "award_date",
            value: formData.award_date,
            onChange: handleChange,
            max: new Date().toLocaleDateString('en-CA'),
          }}
        />
        <InputField
          className="g-col-12"
          label={{ name: "Description" }}
          input={{
            as: "textarea",
            rows: 3,
            name: "description",
            value: formData.description,
            onChange: handleChange,
            placeholder: "Describe the award and its significance",
          }}
        />
        <Form.Group className="g-col-12">
          <Form.Label>Award Image</Form.Label>
          <div className="d-flex align-items-center gap-3">
            <div
              className="award-file-upload border rounded-2 d-flex align-items-center justify-content-center cursor-pointer overflow-hidden"
              style={{ width: "100px", height: "100px", backgroundColor: "#f8f9fa" }}
              onClick={() => fileInputRef.current?.click()}
            >
              {filePreview ? (
                <img src={filePreview} alt="Award Preview" className="w-100 h-100 object-fit-cover" />
              ) : (
                <span className="text-muted small">Upload</span>
              )}
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="d-none"
            />
            {filePreview && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleRemoveFile}
              >
                Remove
              </button>
            )}
          </div>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <CommonButton
          type="button"
          variant="outline"
          title="Cancel"
          onClick={onClose}
          disabled={isLoading}
        />
        <CommonButton
          type="submit"
          variant="primary"
          title={award ? "Update" : "Save"}
          isLoading={isLoading}
        />
      </Modal.Footer>
    </Form>
  );
};

export default withModal(CompanyAwardModalInner);

